import { useState, useEffect } from "react";
import { createTodo, assignTodoToUsers } from "../lib/supabaseHelpers";
import ParticipantSelector from "./expenses/ParticipantSelector";
import type { Participant } from "./expenses/ParticipantSelector";
import { formatISO } from "date-fns";
import { triggerRefetch } from "./TodoList"; // Pfad ggf. anpassen

export function AddTodoModal({
  open,
  onClose,
  tripId,
  participants,
}: {
  open: boolean;
  onClose: () => void;
  tripId: string;
  participants: Participant[];
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState<"before" | "during" | "after">("before");
  const [required, setRequired] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [distributeToAll, setDistributeToAll] = useState(true);

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setPhase("before");
      setRequired(true);
      setDueDate("");
      setSelectedUsers([]);
      setDistributeToAll(true);
    }
  }, [open]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const todo = await createTodo({
        trip_id: tripId,
        title,
        description,
        phase,
        required,
        due_date: dueDate
          ? formatISO(new Date(dueDate), { representation: "date" })
          : undefined,
      });

      // Füge hier die Logik für „Alle Teilnehmer“ ein
      const usersToAssign =
        distributeToAll && selectedUsers.length === 0
          ? participants.map((p) => p.user_id)
          : selectedUsers;

      await assignTodoToUsers(todo.id, usersToAssign);
      triggerRefetch(); // Daten explizit neu laden, um Latenz in der Realtime-Sync abzufangen
      onClose();
    } catch (e) {
      console.error("Fehler beim Speichern", e);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative overflow-y-auto max-h-[90vh]">
        <button
          className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
          onClick={onClose}
          aria-label="Modal schließen"
        >
          &times;
        </button>
        <h2 className="text-sm font-semibold text-slate-100 mb-5">Neue Aufgabe erstellen</h2>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Titel *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Beschreibung</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Phase</label>
        <select
          value={phase}
          onChange={(e) => setPhase(e.target.value as any)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        >
          <option value="before">Vor der Reise</option>
          <option value="during">Während der Reise</option>
          <option value="after">Nach der Reise</option>
        </select>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Frist (optional)</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        />

        <label className="flex items-center gap-2 mb-3 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            className="accent-sky-500"
          />
          Muss-Aufgabe
        </label>

        <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Teilnehmer</label>
        <ParticipantSelector
          participants={participants}
          selected={selectedUsers}
          setSelected={setSelectedUsers}
          distributeToAll={distributeToAll}
          setDistributeToAll={setDistributeToAll}
        />

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !title}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-50"
          >
            {loading ? "Speichern…" : "Aufgabe erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook ebenfalls direkt hier:
export function useAddTodoModal() {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);
  return { open, openModal, closeModal };
}
